import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { TrackingContext } from "context/Tracking";
import { PrimaryButton } from "components/Button/PrimaryButton";

import { buildRiskDiff, mergeRiskDiff } from "./diff";
import { DiffSectionCard } from "./DiffSectionCard";
import { useUpdateAIHazard } from "./hooks/useUpdateAIHazard";
import { AIHazardShell, DiffDecision } from "./types";

interface DiffReviewPanelProps {
  projectId: string;
  // draft AND pending_proposal are both non-null — the detail page only
  // mounts this branch then (incl. after reload: the proposal is persisted).
  hazard: AIHazardShell;
}

// DIFF VIEW (plan §7): replaces the editor while a regeneration proposal is
// unresolved. Apply stays disabled until EVERY changed section carries an
// explicit keep/take decision ("N of M decided"); unchanged sections are
// listed collapsed and marked identical. NO accept-all/reject-all shortcuts
// exist (Mental Model ban). While the proposal is unresolved, Save & review
// is unreachable — the editor is unmounted.
export const DiffReviewPanel = ({ projectId, hazard }: DiffReviewPanelProps) => {
  const { t } = useTranslation();
  const { track } = useContext(TrackingContext);
  const updateHazardMutation = useUpdateAIHazard(projectId);
  const [decisions, setDecisions] = useState<Record<string, DiffDecision>>({});

  const draft = hazard.draft!;
  const proposal = hazard.pending_proposal!;

  const sections = useMemo(
    () => buildRiskDiff(draft, proposal),
    [draft, proposal]
  );

  const changedSections = sections.filter((section) => section.changed);
  const decidedCount = changedSections.filter(
    (section) =>
      decisions[section.key] === "keep" || decisions[section.key] === "take"
  ).length;
  const allDecided = decidedCount === changedSections.length;
  const isBusy = updateHazardMutation.isLoading;

  const handleDecide = (key: string) => (decision: DiffDecision) =>
    setDecisions((previous) => ({ ...previous, [key]: decision }));

  const handleApply = () => {
    if (!allDecided || isBusy) {
      return;
    }

    // mergeRiskDiff additionally THROWS on undecided changed sections —
    // decision-completeness is enforced in the flow, not just this button.
    const merged = mergeRiskDiff(draft, proposal, decisions);
    const acceptedSections = changedSections.filter(
      (section) => decisions[section.key] === "take"
    ).length;
    const rejectedSections = changedSections.length - acceptedSections;

    updateHazardMutation.mutate(
      {
        hazardId: hazard.id,
        patch: { draft: merged, pending_proposal: null },
      },
      {
        onSuccess: () =>
          track("ai risk: diff applied", {
            accepted_sections: acceptedSections,
            rejected_sections: rejectedSections,
            total: changedSections.length,
          }),
      }
    );
  };

  // Explicit decision to drop the whole proposal; the current draft is
  // untouched, so no confirmation dialog is required (plan §7).
  const handleDiscard = () => {
    if (isBusy) {
      return;
    }

    updateHazardMutation.mutate(
      { hazardId: hazard.id, patch: { pending_proposal: null } },
      { onSuccess: () => track("ai risk: diff discarded") }
    );
  };

  return (
    <div className="mt-6 space-y-4">
      <p className="text-sm text-gray-500">{t("risks.ai.diff.subline")}</p>

      {sections.map((section) => (
        <DiffSectionCard
          key={section.key}
          section={section}
          decision={decisions[section.key]}
          onDecide={handleDecide(section.key)}
        />
      ))}

      <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-4">
        <p className="text-sm text-gray-700">
          {t("risks.ai.diff.progress", {
            decided: decidedCount,
            changed: changedSections.length,
          })}
        </p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleDiscard}
            disabled={isBusy}
            className="text-sm text-gray-500 hover:text-red-600 disabled:opacity-50"
          >
            {t("risks.ai.diff.discard.cta")}
          </button>
          <PrimaryButton
            testId="ai-diff-apply"
            onClick={handleApply}
            disabled={!allDecided || isBusy}
          >
            {t("risks.ai.diff.apply.cta")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
